import React from "react";
import Link from "next/link";
import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { RichText } from "prismic-reactjs";
import { useQuery } from "@apollo/client";
import styles from "src/styles/article.scss";
import { PRESS_RELEASE_QUERY } from "src/gql/queries";
import { addApolloState, initializeApollo } from "src/lib/apollo-client";
import { renderBasicSlice } from "src/components/ArticleBody";
import { articleAsText, imgixResize } from "src/services/PrismicService";
import { getPressReleases } from "src/services/PressReleaseService";
import { NewsArticleSchema } from "src/services/SchemaService";
import { getArticleAsUrl } from "src/services/UrlService";

const DEFAULT_IMAGE = "/article/default-image.jpeg";

interface PressReleaseProps {
  slug: string;
}

export const getStaticPaths: GetStaticPaths = async () => {
  const pressReleases = await getPressReleases();
  return {
    paths: pressReleases.map((pressRelease) => ({ params: { slug: pressRelease._meta.uid } })),
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<PressReleaseProps> = async ({ params, previewData }) => {
  // A missing press release goes to the list, the way missing articles go to their news index (SEO request)
  const notFound = { redirect: { destination: "/press", permanent: true } };
  if (typeof params?.slug !== "string") {
    return notFound;
  }
  const apolloClient = initializeApollo();
  const context = previewData ? { headers: { "Prismic-ref": (previewData as { ref: string })?.ref } } : {};
  const res = await apolloClient.query({ query: PRESS_RELEASE_QUERY, variables: { uid: params.slug }, context });
  if (!res?.data?.pressRelease) {
    return notFound;
  }
  return addApolloState<PressReleaseProps>(apolloClient, {
    props: { slug: params.slug },
    revalidate: 10 * 60, // 10 mins
  });
};

const PressRelease = ({ slug }: PressReleaseProps) => {
  const { data } = useQuery(PRESS_RELEASE_QUERY, { variables: { uid: slug } });
  const pressRelease = data?.pressRelease ?? {};
  const { seo_title, seo_description, canonical_url, og_image, meta_tags, body } = pressRelease;

  const publishedDate = pressRelease._meta?.firstPublicationDate;
  const modifiedDate = pressRelease._meta?.lastPublicationDate;

  const title = pressRelease.title ? RichText.asText(pressRelease.title) : "BetQL";
  const subtitle = pressRelease.subtitle ? RichText.asText(pressRelease.subtitle) : "";
  const seoTitle = seo_title || title;
  const imageUrl = og_image?.url ? imgixResize(og_image.url, { width: 1200, height: 630 }) : DEFAULT_IMAGE;
  const pathname = getArticleAsUrl(pressRelease);

  return (
    <div className="article-page-wrapper">
      <div className="rotoql-content-article">
        <NextSeo
          title={seoTitle}
          description={seo_description ?? undefined}
          canonical={canonical_url ?? undefined}
          openGraph={{
            title: seoTitle,
            description: seo_description ?? undefined,
            url: `https://betql.co${pathname}`,
            type: "article",
            article: {
              publishedTime: publishedDate,
              modifiedTime: modifiedDate,
              tags: ["Press Release"],
            },
            images: [
              {
                url: imageUrl,
                width: og_image?.url ? og_image?.dimensions?.width ?? 1200 : 1280,
                height: og_image?.url ? og_image?.dimensions?.height ?? 630 : 720,
                alt: og_image?.alt ?? seoTitle,
              },
            ],
          }}
          additionalMetaTags={(meta_tags ?? [])
            .filter((tag: any) => tag.meta_content && (tag.meta_name || tag.meta_property))
            .map((tag: any) =>
              tag.meta_property
                ? { property: tag.meta_property, content: tag.meta_content }
                : { name: tag.meta_name, content: tag.meta_content }
            )}
        />
        <Link href="/press" legacyBehavior>
          <a className="rotoql-content-article__category-link">Press Releases</a>
        </Link>
        <h1 className="rotoql-content-article__title">{title}</h1>
        {subtitle && <h2 className="rotoql-content-article__subtitle">{subtitle}</h2>}
        {(body ?? []).map((slice: any, index: number) => renderBasicSlice(slice, index, body))}
        <NewsArticleSchema
          author="BetQL"
          datePublished={publishedDate}
          dateModified={modifiedDate}
          headline={title}
          description={(seo_description || subtitle || title).slice(0, 300)}
          articleBody={articleAsText(pressRelease)}
          articleSection="Press Releases"
          image={[imageUrl]}
          keywords={[]}
          pathname={pathname}
        />
        <style jsx global>
          {styles}
        </style>
      </div>
    </div>
  );
};

export default PressRelease;
